import {includes} from 'lodash';
import memoize from 'memoize-one';

import type {BulkEditorProjectTagsData} from '../../state/graphql/runSelectorQuery';
import {
  type Config as TableSettings,
  type MetricValence,
} from '../../util/runfeed';
import * as Run from '../../util/runs';
import type * as RunTypes from '../../util/runTypes';
import * as TableCols from '../../util/tablecols';
import {type DisabledFeaturesMap, type RunSelectorFeature} from './types';
import {type WBTableColumn, type WBTableRowFields} from './WBTable/types';

export const DISABLE_PINNING_AND_BASELINE: DisabledFeaturesMap = {
  baselining: true,
  pinning: true,
};

// basically a strict readonly where the run set is fully locked;
// user can only view list of runs with no other interactions
const RUN_SELECTOR_DISABLE_FEATURES_ALL: Record<RunSelectorFeature, true> = {
  'auto-cols': true,
  'create-sweep': true,
  delete: true,
  'edit-cols': true,
  'edit-run-name': true,
  'edit-run-notes': true,
  export: true,
  filter: true,
  group: true,
  move: true,
  'push-to-run-queue': true,
  search: true,
  sort: true,
  'stop-run': true,
  tag: true,
  'toggle-visibility': true,
  baselining: true,
  pinning: true,
  'pin-cross-project-runs': true,
};

// when user is viewing a workspace where they only have read access for the project
export const PROJECT_READONLY_WORKSPACE_RUN_SELECTOR_DISABLED_FEATURES: DisabledFeaturesMap =
  {
    'create-sweep': true,
    delete: true,
    'edit-run-name': true,
    'edit-run-notes': true,
    move: true,
    'stop-run': true,
    tag: true,
    'pin-cross-project-runs': true,
  };

export type RunActionPermissions = {
  canMoveRun: boolean;
  canDeleteRun: boolean;
  canUpdateRun: boolean;
  canStopRun: boolean;
};

// Disabled-feature map for a read-only project workspace, refined by the viewer's
// per-action run:* permissions. A custom role inheriting from Viewer can be granted
// individual run permissions; each corresponding action stays enabled when granted.
// Non-run actions (tag, create-sweep, pin-cross-project-runs) have no run:* equivalent
// and remain disabled. When the viewer holds none of the run permissions this returns
// exactly PROJECT_READONLY_WORKSPACE_RUN_SELECTOR_DISABLED_FEATURES, so default Viewers
// see no behavior change. Mutation-level auth remains the source of truth.
export function readOnlyRunSelectorDisabledFeatures(
  perms: RunActionPermissions
): DisabledFeaturesMap {
  return {
    move: !perms.canMoveRun,
    delete: !perms.canDeleteRun,
    'edit-run-name': !perms.canUpdateRun,
    'edit-run-notes': !perms.canUpdateRun,
    'stop-run': !perms.canStopRun,
    tag: true,
    'create-sweep': true,
    'pin-cross-project-runs': true,
  };
}

export const PUBLISHED_REPORT_RUN_SELECTOR_DISABLED_FEATURES: DisabledFeaturesMap =
  {
    ...RUN_SELECTOR_DISABLE_FEATURES_ALL,
    'toggle-visibility': false,
  };

export const CREATE_SWEEP_PAGE_RUN_SELECTOR_DISABLED_FEATURES: DisabledFeaturesMap =
  {
    'create-sweep': true,
    delete: true,
    'edit-run-name': true,
    'stop-run': true,
    'toggle-visibility': true,
    baselining: true,
    pinning: true,
    'pin-cross-project-runs': true,
  };

export const SWEEP_PRIOR_RUNS_DISABLED_FEATURES: DisabledFeaturesMap =
  RUN_SELECTOR_DISABLE_FEATURES_ALL;

export type RunToGetRow = Run.RunWithSupportedKeys & {id: string};

export type RunSelectorRowType<T extends RunToGetRow> = WBTableRowFields<
  T & {
    runsetInfo: {id: string; name: string};
    selectedRunName: string | undefined;
  }
>;

export function getRows<T extends RunToGetRow>(
  runs: T[],
  selectedRunName: string | undefined,
  runSet: {id: string; name: string}
): RunSelectorRowType<T>[] {
  return runs.map(r => {
    return {
      ...r,
      selectedRunName,
      runsetInfo: {
        id: runSet.id,
        name: runSet.name,
      },
      __address__: TableCols.makeRowAddressComponent(r.id, runSet.id),
    };
  });
}

function getColumns(
  filteredKeys: RunTypes.Key[],
  defaultKeys: RunTypes.Key[],
  tableSettings: TableSettings,
  renderHeader: WBTableColumn['renderHeader'],
  renderCell: WBTableColumn['renderCell']
) {
  const filteredKeyStrings = filteredKeys.map(k => Run.keyToString(k));
  const orderKeys = tableSettings.columnOrder
    .filter(k => !includes(filteredKeyStrings, k))
    .map(k => Run.keyFromString(k)!);

  const unionedKeys = TableCols.unionKeys(defaultKeys, [
    ...filteredKeys,
    ...orderKeys,
  ]);

  return unionedKeys.map(key => {
    const isDisplayNameCol = Run.keysEqual(key, {
      section: 'run',
      name: 'displayName',
    });

    return TableCols.keyToColumn(key, {
      renderHeader: isDisplayNameCol ? renderHeader : undefined,
      renderCell,
    });
  });
}

function getTags(bulkTagsProject: BulkEditorProjectTagsData | undefined) {
  if (!bulkTagsProject) {
    return [];
  }

  const counts: {[key: string]: number} = {};

  (bulkTagsProject.tagCounts || []).forEach(t => {
    counts[t.name] = t.count;
  });

  return (bulkTagsProject.tags || []).map(tag => ({
    name: tag.name,
    colorIndex: tag.colorIndex,
    count: counts[tag.name] || 0,
  }));
}

export const getColumnsMemoized = memoize(getColumns);
export const getTagsMemoized = memoize(getTags);

type RunLike = {id: string};

export const isRunBaseline = (
  baselineRunId: string | null | undefined,
  run: RunLike
) => baselineRunId === run.id;

/**
 * Calculates the difference between a current metric value and a baseline.
 * Used to show how a run's metrics compare to the baseline in the run table.
 *
 * @param baselineValue - The baseline run metric value.
 * @param currentValue - The run metric value we are comparing to the baseline.
 * @returns currentValue - baselineValue, or null if either is not a number.
 */
export function calculateMetricDelta(
  baselineValue: RunTypes.Value | undefined,
  currentValue: RunTypes.Value | undefined
): number | null {
  if (typeof baselineValue !== 'number' || typeof currentValue !== 'number') {
    return null;
  }
  return currentValue - baselineValue;
}

/**
 * Determines the visual tone (positive/negative/neutral) for displaying a metric delta
 * based on the metric's valence (whether lower or higher values are better).
 *
 * @param valence - The metric's valence indicating optimization direction:
 *   - 'lower-is-better': metrics like loss, error rate (negative delta = good)
 *   - 'higher-is-better': metrics like accuracy, score (positive delta = good)
 *   - 'neutral': no preference, always returns 'neutral'
 * @param delta - The difference between current and baseline values, or null if unavailable.
 * @returns The display tone:
 *   - 'positive': the delta represents an improvement
 *   - 'negative': the delta represents a regression
 *   - 'neutral': no change, or valence/delta is neutral/null
 */
export function calculateMetricTone(
  valence: MetricValence,
  delta: number | null
): 'positive' | 'negative' | 'neutral' {
  if (delta === null || valence === 'neutral') {
    return 'neutral';
  }
  if (delta < 0) {
    return valence === 'lower-is-better' ? 'positive' : 'negative';
  }
  if (delta > 0) {
    return valence === 'lower-is-better' ? 'negative' : 'positive';
  }
  return 'neutral';
}

export function isCrossProjectRun(
  rowProject: {entityName: string; name: string} | undefined,
  runsetProject: {entityName: string; name: string}
) {
  if (!rowProject) {
    return false;
  }

  // Project and entity names are case-insensitive at the data layer,
  // so comparisons here must lowercase both sides. Otherwise URL casing
  // that differs from the stored canonical casing produces false
  // cross-project matches.
  return (
    rowProject.entityName.toLowerCase() !==
      runsetProject.entityName.toLowerCase() ||
    rowProject.name.toLowerCase() !== runsetProject.name.toLowerCase()
  );
}
